import { DecimalPipe, KeyValuePipe } from '@angular/common';
import { Component, DestroyRef, computed, inject, input, linkedSignal, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, take } from 'rxjs/operators';

import {
  INSIGHT_TEXT_FIELDS,
  INSIGHT_TEXT_FIELD_LABELS,
  InsightTextField,
} from '../../contributions/insight-base.model';
import { InsightExtended } from '../../contributions/insight-extended.model';
import { NormalisedHttpError } from '../../error';
import { SUPPORTED_LOCALES, SupportedLocale } from '../../language/locale.model';
import { LoadingObject, LoadingService } from '../../loaders';
import { EditorialService } from '../editorial.service';
import {
  ContentFormat,
  SendInsightForTranslationRequest,
  SendInsightForTranslationResponse,
  TranslationRunResult,
  isFailedTranslationResult,
} from '../insight-translation.model';

/**
 * One result flattened for display. The API's results are a discriminated union, which a
 * template cannot narrow, so the union is resolved here and `error` alone distinguishes a
 * failure — a `200` can still carry failed results.
 */
interface TranslationResultView {
  key: string;
  field: InsightTextField;
  fieldLabel: string;
  targetLang: SupportedLocale;
  contentFormat: ContentFormat;
  sourceChars: number;
  committed: boolean;
  error: string | null;
  text: string;
  billedCharacters: number | null;
  durationMs: number | null;
  modelTypeUsed: string | null;
}

/**
 * The UI for `POST api/v1/insights/{id}/send-for-translation` — an editorial user
 * translating one insight into one or more languages.
 *
 * It offers no choice of translation strategy, and should not: how a field travels to the
 * provider is settled, and derived on the backend from the format the model declares for
 * that field. What the user chooses is *what* to translate, not *how*.
 *
 * Two properties of the endpoint shape this component. A run is synchronous and slow — one
 * provider round-trip per field × language, in sequence — so it shows real elapsed time
 * rather than a bare spinner. And `dry_run` still calls the provider, so previewing and
 * then saving bills twice; that is why the preview is not the automatic first step.
 */
@Component({
  selector: 'multisite-insight-translation',
  imports: [DecimalPipe, KeyValuePipe],
  templateUrl: './insight-translation.component.html',
  styleUrls: ['./insight-translation.component.scss'],
})
export class InsightTranslationComponent {
  insight = input.required<InsightExtended>();
  closeRequest = output<void>();
  /** Emitted after a run that wrote to the insight, so the caller can refresh its data. */
  committed = output<void>();

  private editorialService = inject(EditorialService);
  private loadingService = inject(LoadingService);
  private destroyRef = inject(DestroyRef);

  constructor() {
    // The panel can be closed mid-run; the request is abandoned by `takeUntilDestroyed`,
    // so the elapsed-time interval has to go with it.
    this.destroyRef.onDestroy(() => this.stopElapsedTimer());
  }

  readonly supportedLocales = SUPPORTED_LOCALES;
  readonly textFields = INSIGHT_TEXT_FIELDS;
  readonly fieldLabels = INSIGHT_TEXT_FIELD_LABELS;
  readonly maxContextLength = 500;

  loadingObject = signal<LoadingObject>({});

  isAnythingLoading = computed<boolean>(() => this.loadingService.isAnythingLoading(this.loadingObject()));

  setLoadingStatus = (key: string, value: boolean): void => {
    this.loadingObject.update((current) => this.loadingService.setLoadingStatus(key, value, current));
  };

  // --- Request options -------------------------------------------------------------------

  /**
   * English is this application's master language, so it is the source unless the insight
   * holds no English text at all — then the first locale it does hold is the only thing
   * there is to translate from. `translated_locales` arrays are unordered sets, so "first"
   * means first in the fixed UI-locale order, not first in the stored array.
   */
  readonly defaultSourceLang = computed<SupportedLocale>(() => {
    const translatedLocales = this.insight()?.translated_locales;
    // `concat`, not `flatMap`: the workspace's base `lib` is es2017, where `flatMap` (es2019)
    // does not exist — libs/common compiles it, but the apps consuming this do not.
    const localesHeld = INSIGHT_TEXT_FIELDS.reduce<SupportedLocale[]>(
      (accumulator, field) => accumulator.concat(translatedLocales?.[field] ?? []),
      [],
    );

    if (localesHeld.includes('en')) {
      return 'en';
    }

    return SUPPORTED_LOCALES.find((locale) => localesHeld.includes(locale)) ?? 'en';
  });

  /** Seeded from {@link defaultSourceLang}, and freely overridable — every locale stays on offer. */
  sourceLang = linkedSignal<SupportedLocale>(() => this.defaultSourceLang());
  targetLangs = signal<SupportedLocale[]>([]);
  /** Empty means "omit `fields`" — the backend then translates every field with content. */
  selectedFields = signal<InsightTextField[]>([]);
  context = signal<string>('');
  /** Off means `dry_run: true`. Nothing is written to the insight unless this is ticked. */
  saveToInsight = signal<boolean>(false);

  // --- Run state -------------------------------------------------------------------------

  runResponse = signal<SendInsightForTranslationResponse | null>(null);
  runError = signal<NormalisedHttpError | null>(null);
  validationMessage = signal<string | null>(null);
  /** A run that will write to the insight waits here for a second, explicit confirmation. */
  awaitingCommitConfirmation = signal<boolean>(false);
  elapsedSeconds = signal<number>(0);
  private expandedResultKeys = signal<Record<string, boolean>>({});
  private elapsedTimerId: ReturnType<typeof setInterval> | null = null;

  readonly availableTargetLocales = computed<SupportedLocale[]>(() =>
    SUPPORTED_LOCALES.filter((locale) => locale !== this.sourceLang()),
  );

  /** The API rejects a source language that also appears in the targets, so it never can. */
  readonly effectiveTargetLangs = computed<SupportedLocale[]>(() =>
    this.targetLangs().filter((locale) => locale !== this.sourceLang()),
  );

  readonly results = computed<TranslationResultView[]>(() =>
    (this.runResponse()?.results ?? []).map((result) => this.toResultView(result)),
  );

  private toResultView(result: TranslationRunResult): TranslationResultView {
    const base = {
      key: `${result.field}::${result.target_lang}`,
      field: result.field,
      fieldLabel: INSIGHT_TEXT_FIELD_LABELS[result.field],
      targetLang: result.target_lang,
      contentFormat: result.content_format,
      sourceChars: result.source_chars,
      committed: result.committed,
    };

    if (isFailedTranslationResult(result)) {
      return { ...base, error: result.error, text: '', billedCharacters: null, durationMs: null, modelTypeUsed: null };
    }

    return {
      ...base,
      error: null,
      text: result.text,
      billedCharacters: result.billed_characters,
      durationMs: result.duration_ms,
      modelTypeUsed: result.model_type_used,
    };
  }

  readonly failedResultCount = computed<number>(
    () => (this.runResponse()?.results ?? []).filter((result) => isFailedTranslationResult(result)).length,
  );

  /** Provider-billed characters across the run — what this run cost. */
  readonly totalBilledCharacters = computed<number>(() =>
    (this.runResponse()?.results ?? []).reduce(
      (total, result) => total + (isFailedTranslationResult(result) ? 0 : result.billed_characters ?? 0),
      0,
    ),
  );

  isResultExpanded(resultKey: string): boolean {
    return !!this.expandedResultKeys()[resultKey];
  }

  toggleResultExpanded(resultKey: string): void {
    this.expandedResultKeys.update((current) => ({ ...current, [resultKey]: !current[resultKey] }));
  }

  // --- Option toggles --------------------------------------------------------------------

  onSourceLangChange(locale: string): void {
    if (!SUPPORTED_LOCALES.includes(locale as SupportedLocale)) {
      return;
    }

    this.sourceLang.set(locale as SupportedLocale);
    this.targetLangs.update((current) => current.filter((target) => target !== locale));
  }

  isTargetLangSelected(locale: SupportedLocale): boolean {
    return this.targetLangs().includes(locale);
  }

  toggleTargetLang(locale: SupportedLocale): void {
    this.targetLangs.update((current) =>
      current.includes(locale) ? current.filter((item) => item !== locale) : [...current, locale],
    );
    this.validationMessage.set(null);
  }

  isFieldSelected(field: InsightTextField): boolean {
    return this.selectedFields().includes(field);
  }

  toggleField(field: InsightTextField): void {
    this.selectedFields.update((current) =>
      current.includes(field) ? current.filter((item) => item !== field) : [...current, field],
    );
  }

  onContextChange(value: string): void {
    this.context.set(value.slice(0, this.maxContextLength));
  }

  onSaveToInsightChange(shouldSave: boolean): void {
    this.saveToInsight.set(shouldSave);
    this.awaitingCommitConfirmation.set(false);
  }

  // --- Running ---------------------------------------------------------------------------

  onSubmit(): void {
    if (!this.effectiveTargetLangs().length) {
      this.validationMessage.set('Choose at least one target language.');
      return;
    }

    this.validationMessage.set(null);

    // Writing to the insight is irreversible from here, so make it a two-step action.
    if (this.saveToInsight() && !this.awaitingCommitConfirmation()) {
      this.awaitingCommitConfirmation.set(true);
      return;
    }

    this.run();
  }

  cancelCommitConfirmation(): void {
    this.awaitingCommitConfirmation.set(false);
  }

  onClose(): void {
    this.closeRequest.emit();
  }

  buildRequest(): SendInsightForTranslationRequest {
    const fields = this.selectedFields();
    const context = this.context().trim();

    return {
      target_langs: this.effectiveTargetLangs(),
      source_lang: this.sourceLang(),
      dry_run: !this.saveToInsight(),
      ...(fields.length ? { fields } : {}),
      ...(context ? { context } : {}),
    };
  }

  private run(): void {
    const insightId = this.insight().id;
    const requestKey = this.getRequestKey(insightId);

    this.awaitingCommitConfirmation.set(false);
    this.runResponse.set(null);
    this.runError.set(null);
    this.expandedResultKeys.set({});
    this.startElapsedTimer();
    this.setLoadingStatus(requestKey, true);

    this.editorialService
      .sendInsightForTranslation(insightId, this.buildRequest())
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.setLoadingStatus(requestKey, false);
          this.stopElapsedTimer();
        }),
      )
      .subscribe({
        next: (response) => {
          this.runResponse.set(response);

          if (response?.committed) {
            this.committed.emit();
          }
        },
        error: (error: NormalisedHttpError) => {
          this.runError.set(error ?? { message: 'error.something_went_wrong' });
        },
      });
  }

  private getRequestKey(insightId: number): string {
    return `category:insight-translation::type:${insightId}`;
  }

  /** The run is slow and synchronous, so show real elapsed time rather than a bare spinner. */
  private startElapsedTimer(): void {
    this.stopElapsedTimer();
    this.elapsedSeconds.set(0);
    this.elapsedTimerId = setInterval(() => this.elapsedSeconds.update((seconds) => seconds + 1), 1000);
  }

  private stopElapsedTimer(): void {
    if (this.elapsedTimerId !== null) {
      clearInterval(this.elapsedTimerId);
      this.elapsedTimerId = null;
    }
  }
}
